/**
 * SchoolCommunications — staff-side inbox for parent messages.
 *
 * Gated by canManageParentSettings (admin / head / front office).
 * Lets staff:
 *   - See all parent threads, filter by status & category
 *   - Open a thread, read the conversation, reply
 *   - Mark resolved / reopen
 *   - For detail-update threads: review proposed changes and
 *     Approve (calls approveParentDetailChange Cloud Function, which
 *     writes the student record server-side) or Reject
 *
 * The school side NEVER writes to student records directly — approval
 * goes through the Cloud Function. This screen only writes to the
 * reportParentMessages thread + its posts subcollection.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  MessageSquare,
  Send,
  ChevronLeft,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Pencil,
  Inbox,
} from 'lucide-react';
import { db, functions, auth } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { GradientHero, SoftCard, AccentButton, glassFieldClass } from '@/components/ui/glass';
import { Button } from '@/components/ui/primitives';
import {
  CATEGORY_LABELS,
  STATUS_LABELS,
  type MessageCategory,
  type ThreadStatus,
  type ParentMessageThread,
  type ParentMessagePost,
} from './types';

export default function SchoolCommunications({
  restrictCategory,
  embedded = false,
}: { restrictCategory?: MessageCategory; embedded?: boolean } = {}) {
  const { userData } = useAuth();
  const staffUid = userData?.uid ?? auth.currentUser?.uid ?? '';
  const staffName = userData?.displayName ?? 'School';

  const [threads, setThreads] = useState<ParentMessageThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ThreadStatus | 'all' | 'unread'>('all');
  const [categoryFilter, setCategoryFilter] = useState<MessageCategory | 'all'>('all');

  /* ---------- load all threads (staff see everything) ---------- */
  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, 'reportParentMessages'),
      orderBy('lastMessageAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const out: ParentMessageThread[] = [];
        snap.forEach((d) =>
          out.push({ id: d.id, ...(d.data() as Omit<ParentMessageThread, 'id'>) }),
        );
        setThreads(out);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    return threads.filter((t) => {
      if (restrictCategory && t.category !== restrictCategory) return false;
      if (statusFilter === 'unread' && !t.unreadBySchool) return false;
      if (statusFilter !== 'all' && statusFilter !== 'unread' && t.status !== statusFilter)
        return false;
      if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
      return true;
    });
  }, [threads, statusFilter, categoryFilter, restrictCategory]);

  const unreadCount = useMemo(
    () => threads.filter((t) => t.unreadBySchool && (!restrictCategory || t.category === restrictCategory)).length,
    [threads, restrictCategory],
  );

  const activeThread = useMemo(
    () => threads.find((t) => t.id === activeId) ?? null,
    [threads, activeId],
  );

  if (activeThread) {
    return (
      <SchoolThreadView
        thread={activeThread}
        staffUid={staffUid}
        staffName={staffName}
        onBack={() => setActiveId(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {!embedded && (
        <GradientHero
          icon={MessageSquare}
          title="Communications"
          subtitle="Messages from parents — consultations, queries, and detail requests"
        />
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          className={glassFieldClass('w-auto')}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ThreadStatus | 'all' | 'unread')}
        >
          <option value="all">All statuses</option>
          <option value="unread">Unread{unreadCount > 0 ? ` (${unreadCount})` : ''}</option>
          <option value="awaiting_school">Awaiting school</option>
          <option value="awaiting_parent">Awaiting parent</option>
          <option value="resolved">Resolved</option>
          <option value="archived">Archived</option>
        </select>
        {!restrictCategory && (
          <select
            className={glassFieldClass('w-auto')}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as MessageCategory | 'all')}
          >
            <option value="all">All categories</option>
            {(Object.keys(CATEGORY_LABELS) as MessageCategory[]).map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        )}
      </div>

      {loading ? (
        <SoftCard>
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        </SoftCard>
      ) : filtered.length === 0 ? (
        <SoftCard>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Inbox size={32} className="text-slate-300 dark:text-slate-600 mb-2" />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {threads.length === 0
                ? 'No parent messages yet.'
                : 'No messages match these filters.'}
            </p>
          </div>
        </SoftCard>
      ) : (
        <div className="space-y-2">
          {filtered.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveId(t.id)}
              className="w-full text-left"
            >
              <SoftCard className="transition-shadow hover:shadow-md">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {t.unreadBySchool && (
                        <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                      )}
                      <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {t.subject}
                      </span>
                      {t.category === 'detail_update' && t.changeStatus === 'pending' && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 shrink-0">
                          change pending
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {t.parentName} · re {t.studentName} · {CATEGORY_LABELS[t.category]}
                      {t.subjectContextLabel ? ` · ${t.subjectContextLabel}` : ''}
                    </p>
                    {t.lastMessagePreview && (
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 truncate">
                        {t.lastMessageSide === 'school' ? 'School: ' : `${t.parentName}: `}
                        {t.lastMessagePreview}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <SchoolStatusBadge status={t.status} />
                    <span className="text-[10px] text-slate-400">
                      {t.lastMessageAt
                        ? new Date(t.lastMessageAt).toLocaleDateString()
                        : ''}
                    </span>
                  </div>
                </div>
              </SoftCard>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- status badge ---------------- */
function SchoolStatusBadge({ status }: { status: ThreadStatus }) {
  const map: Record<string, string> = {
    awaiting_school: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    awaiting_parent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    resolved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    archived: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full ${map[status] ?? map.archived}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

/* ===================================================================
   THREAD VIEW (school side)
   =================================================================== */
interface SchoolThreadViewProps {
  thread: ParentMessageThread;
  staffUid: string;
  staffName: string;
  onBack: () => void;
}

function SchoolThreadView({ thread, staffUid, staffName, onBack }: SchoolThreadViewProps) {
  const [posts, setPosts] = useState<ParentMessagePost[]>([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [actioning, setActioning] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'reportParentMessages', thread.id, 'posts'),
      orderBy('createdAt', 'asc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      const out: ParentMessagePost[] = [];
      snap.forEach((d) =>
        out.push({ id: d.id, ...(d.data() as Omit<ParentMessagePost, 'id'>) }),
      );
      setPosts(out);
    });
    return unsub;
  }, [thread.id]);

  // Clear the school unread flag on open
  useEffect(() => {
    if (thread.unreadBySchool) {
      updateDoc(doc(db, 'reportParentMessages', thread.id), {
        unreadBySchool: false,
      }).catch(() => {});
    }
  }, [thread.id, thread.unreadBySchool]);

  async function sendReply() {
    if (sending || !reply.trim()) return;
    setErr(null);
    setSending(true);
    try {
      const nowIso = new Date().toISOString();
      await addDoc(collection(db, 'reportParentMessages', thread.id, 'posts'), {
        authorUid: staffUid,
        authorName: staffName,
        authorSide: 'school',
        text: reply.trim(),
        createdAt: nowIso,
      });
      await updateDoc(doc(db, 'reportParentMessages', thread.id), {
        status: 'awaiting_parent',
        updatedAt: nowIso,
        lastMessageAt: nowIso,
        lastMessageSide: 'school',
        lastMessagePreview: reply.trim().slice(0, 120),
        unreadByParent: true,
        unreadBySchool: false,
      });
      setReply('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not send reply.');
    } finally {
      setSending(false);
    }
  }

  async function setStatus(status: ThreadStatus) {
    try {
      await updateDoc(doc(db, 'reportParentMessages', thread.id), {
        status,
        updatedAt: new Date().toISOString(),
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not update status.');
    }
  }

  async function approveChange() {
    if (actioning) return;
    setActionMsg(null);
    setActioning(true);
    try {
      const fn = httpsCallable(functions, 'approveParentDetailChange');
      const res = await fn({ threadId: thread.id });
      const data = res.data as { ok: boolean; applied: Array<{ field: string }> };
      setActionMsg({
        ok: true,
        text: `Applied ${data.applied?.length ?? 0} change(s) to the student record.`,
      });
    } catch (e) {
      setActionMsg({
        ok: false,
        text: e instanceof Error ? e.message : 'Approval failed.',
      });
    } finally {
      setActioning(false);
    }
  }

  async function rejectChange() {
    if (actioning) return;
    setActionMsg(null);
    setActioning(true);
    try {
      const nowIso = new Date().toISOString();
      // Staff may set changeStatus to 'rejected' from the client (rules allow it)
      await updateDoc(doc(db, 'reportParentMessages', thread.id), {
        changeStatus: 'rejected',
        status: 'resolved',
        updatedAt: nowIso,
        lastMessageAt: nowIso,
        lastMessageSide: 'school',
        lastMessagePreview: 'Detail change request was not approved.',
        unreadByParent: true,
        unreadBySchool: false,
      });
      await addDoc(collection(db, 'reportParentMessages', thread.id, 'posts'), {
        authorUid: staffUid,
        authorName: staffName,
        authorSide: 'school',
        text:
          'After review, the requested detail change has not been applied. Please contact the school office if you would like to discuss this.',
        createdAt: nowIso,
        system: true,
      });
      setActionMsg({ ok: true, text: 'Change request rejected.' });
    } catch (e) {
      setActionMsg({
        ok: false,
        text: e instanceof Error ? e.message : 'Could not reject.',
      });
    } finally {
      setActioning(false);
    }
  }

  const isPendingChange =
    thread.category === 'detail_update' && thread.changeStatus === 'pending';

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 flex items-center gap-1"
      >
        <ChevronLeft size={16} /> Back to inbox
      </button>

      <SoftCard>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">
              {thread.subject}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              From {thread.parentName} ({thread.parentEmail}) · re{' '}
              <span className="font-medium">{thread.studentName}</span> ·{' '}
              {CATEGORY_LABELS[thread.category]}
              {thread.subjectContextLabel ? ` · ${thread.subjectContextLabel}` : ''}
            </p>
          </div>
          <SchoolStatusBadge status={thread.status} />
        </div>

        {/* Detail-change review panel */}
        {thread.category === 'detail_update' && thread.proposedChanges && (
          <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200 mb-2">
              <Pencil size={12} /> Requested detail changes for {thread.studentName}
              {thread.changeStatus && thread.changeStatus !== 'pending' && (
                <span
                  className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${
                    thread.changeStatus === 'approved'
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                  }`}
                >
                  {thread.changeStatus}
                  {thread.changeActionedBy ? ` · ${thread.changeActionedBy}` : ''}
                </span>
              )}
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-400">
                  <th className="text-left font-medium pb-1">Field</th>
                  <th className="text-left font-medium pb-1">New value</th>
                </tr>
              </thead>
              <tbody>
                {thread.proposedChanges.map((c, i) => (
                  <tr key={i} className="border-t border-slate-200 dark:border-slate-700">
                    <td className="py-1.5 pr-3 text-slate-600 dark:text-slate-300">
                      {c.fieldLabel}
                    </td>
                    <td className="py-1.5 font-medium text-slate-900 dark:text-slate-100">
                      {c.newValue}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {actionMsg && (
              <div
                className={`mt-3 text-xs flex items-center gap-1.5 ${
                  actionMsg.ok
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-red-700 dark:text-red-300'
                }`}
              >
                {actionMsg.ok ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />}
                {actionMsg.text}
              </div>
            )}

            {isPendingChange && (
              <div className="flex items-center gap-2 mt-3">
                <AccentButton onClick={approveChange} disabled={actioning}>
                  {actioning ? (
                    <span className="flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> Working…
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CheckCircle2 size={15} /> Approve &amp; apply
                    </span>
                  )}
                </AccentButton>
                <Button variant="ghost" onClick={rejectChange} disabled={actioning}>
                  <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                    <XCircle size={15} /> Reject
                  </span>
                </Button>
              </div>
            )}
            {isPendingChange && (
              <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-2">
                Approving writes these values to {thread.studentName}’s record and notifies the parent.
              </p>
            )}
          </div>
        )}
      </SoftCard>

      {/* Conversation */}
      <div className="space-y-3">
        {posts.map((p) => {
          const school = p.authorSide === 'school';
          return (
            <div key={p.id} className={`flex ${school ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                  school
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100'
                }`}
              >
                <p className="text-xs font-semibold mb-0.5 opacity-80">
                  {school ? p.authorName : thread.parentName}
                </p>
                <p className="text-sm whitespace-pre-wrap">{p.text}</p>
                <p className={`text-[10px] mt-1 ${school ? 'text-white/60' : 'text-slate-400'}`}>
                  {new Date(p.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply + actions */}
      <SoftCard>
        {err && (
          <div className="text-sm text-red-700 dark:text-red-300 flex items-center gap-2 mb-2">
            <AlertCircle size={14} /> {err}
          </div>
        )}
        <textarea
          className={glassFieldClass('min-h-[90px]')}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Write a reply to the parent…"
        />
        <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {thread.status !== 'resolved' && (
              <Button variant="ghost" onClick={() => setStatus('resolved')}>
                <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 size={15} /> Mark resolved
                </span>
              </Button>
            )}
            {thread.status === 'resolved' && (
              <Button variant="ghost" onClick={() => setStatus('awaiting_school')}>
                Reopen
              </Button>
            )}
            {thread.status !== 'archived' && (
              <Button variant="ghost" onClick={() => setStatus('archived')}>
                Archive
              </Button>
            )}
          </div>
          <AccentButton onClick={sendReply} disabled={!reply.trim() || sending}>
            {sending ? (
              <span className="flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Sending…
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send size={16} /> Send reply
              </span>
            )}
          </AccentButton>
        </div>
      </SoftCard>
    </div>
  );
}
